import { useState } from 'react'
import { NavLink, useLocation, useSearchParams } from 'react-router-dom'
import {
  CircleHelp,
  Compass,
  MessageSquare,
  MoreHorizontal,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { LogoutButton } from '@/components/auth/LogoutButton'
import { AdminNavLink } from '@/components/layout/AdminNavLink'
import { useMinCopy } from '@/i18n/useUiLocale'
import { cn } from '@/components/ui/cn'

export function MobileMoreMenu() {
  const [open, setOpen] = useState(false)
  const { nav } = useMinCopy()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const feedbackActive =
    location.pathname === '/settings' && searchParams.get('tab') === 'feedback'

  const moreItems: Array<{ to: string; label: string; icon: LucideIcon; testId?: string }> = [
    { to: '/tests', label: nav.challenge, icon: Target, testId: 'mobile-nav-tests' },
    { to: '/catalog', label: nav.catalog, icon: Compass, testId: 'mobile-nav-catalog' },
    { to: '/progress', label: nav.progress, icon: TrendingUp, testId: 'mobile-nav-progress' },
    { to: '/ai-practice', label: nav.practice, icon: Sparkles, testId: 'mobile-nav-practice' },
    { to: '/settings', label: nav.settings, icon: Settings, testId: 'mobile-nav-settings' },
    { to: '/settings?tab=feedback', label: nav.feedback, icon: MessageSquare, testId: 'mobile-nav-feedback' },
    { to: '/faq', label: nav.faq, icon: CircleHelp, testId: 'mobile-nav-faq' },
  ]

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-14 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium text-muted-foreground transition-colors"
        data-testid="mobile-nav-more"
        aria-label={nav.more}
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
        <span className="leading-none">{nav.more}</span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label={nav.closeMenuAria}
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 rounded-t-2xl border-t border-border bg-card p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">{nav.more}</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted"
                aria-label={nav.close}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-1">
              {moreItems.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => {
                    const active =
                      to.includes('tab=feedback') ? feedbackActive : isActive && !to.includes('?')
                    return cn(
                      'flex min-h-12 items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors',
                      active ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted',
                    )
                  }}
                >
                  <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  {label}
                </NavLink>
              ))}
              <AdminNavLink onNavigate={() => setOpen(false)} />
              <div className="mt-2 border-t border-border pt-3">
                <LogoutButton
                  fullWidth
                  showIcon
                  className="border border-border bg-background text-foreground hover:bg-muted"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
